import httpClient from '../httpClient';

export interface ExternalCertificate {
  id: number;
  user_id: string;
  certificate_name: string;
  issuing_organization: string;
  issue_date: string;
  expiry_date: string | null;
  credential_id: string | null;
  certificate_link: string | null;
  file_path: string | null;
  logo_path: string | null;
  issued_by_org: boolean;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface ExternalCertificateResponse {
  success: boolean;
  data?: {
    data: ExternalCertificate[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  message?: string;
  error?: {
    message: string;
    code: string;
  };
}

export interface CertificateStatsResponse {
  success: boolean;
  data?: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  message?: string;
  error?: {
    message: string;
    code: string;
  };
}

export interface ActionResponse {
  success: boolean;
  data?: any;
  message?: string;
  error?: {
    message: string;
    code: string;
  };
}

/**
 * Get all external certificates with filters
 */
export const getAllExternalCertificates = async (
  status: 'all' | 'pending' | 'approved' | 'rejected' = 'all',
  page: number = 1,
  limit: number = 10,
  search: string = ''
): Promise<ExternalCertificateResponse> => {
  try {
    const response = await httpClient.get<ExternalCertificateResponse>(
      '/admin/external-certificates',
      {
        params: { status, page, limit, search }
      }
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.error?.message || 'Failed to fetch external certificates',
        code: error.response?.data?.error?.code || 'FETCH_ERROR'
      }
    };
  }
};

/**
 * Approve external certificate
 */
export const approveCertificate = async (certificateId: number): Promise<ActionResponse> => {
  try {
    const response = await httpClient.post<ActionResponse>(
      `/admin/external-certificates/${certificateId}/approve`
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.error?.message || 'Failed to approve certificate',
        code: error.response?.data?.error?.code || 'APPROVE_ERROR'
      }
    };
  }
};

/**
 * Reject external certificate
 */
export const rejectCertificate = async (certificateId: number): Promise<ActionResponse> => {
  try {
    const response = await httpClient.post<ActionResponse>(
      `/admin/external-certificates/${certificateId}/reject`
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.error?.message || 'Failed to reject certificate',
        code: error.response?.data?.error?.code || 'REJECT_ERROR'
      }
    };
  }
};

/**
 * Get certificate statistics
 */
export const getCertificateStats = async (): Promise<CertificateStatsResponse> => {
  try {
    const response = await httpClient.get<CertificateStatsResponse>(
      '/admin/external-certificates/stats'
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.error?.message || 'Failed to fetch statistics',
        code: error.response?.data?.error?.code || 'STATS_ERROR'
      }
    };
  }
};
